/**
 * RamadanDashboard — Ramadan-only widget combining iftar/suhoor countdowns,
 * tarāwīḥ tracker, and a daily du'a-of-the-day pulled from authentic
 * sources. Renders only when the Hijri month is Ramadan (9).
 *
 * Sections:
 *   1. Big countdown to NEXT relevant moment:
 *      - Before fajr → "Suhoor ends in HH:MM" (uses Fajr time)
 *      - Day → "Iftar in HH:MM" (uses Maghrib time)
 *      - Evening (after maghrib) → "Tarawih starts in HH:MM" (uses Isha)
 *      - Late night → "Suhoor recommended in HH:MM"
 *   2. Day-of-Ramadan progress (Day 7 of 30) with progress bar
 *   3. Tarāwīḥ rakaat tracker — tap to add 2 rak'ah at a time, persists per
 *      Ramadan day
 *   4. Daily du'a of Ramadan (Bukhari/Muslim sourced, day-deterministic)
 *
 * State persists per Hijri date so the tracker resets each new day. The
 * widget consumes prayer times from PrayerTimeService — same source as
 * the Hero clock — so accuracy is consistent across the app.
 */

import type { Widget, WidgetContext } from '../types';
import { gregorianToHijri } from '../../../services/HijriDate';
import { prayerTimeService } from '../../../services/PrayerTimeService';

interface RamadanDua {
  arabic: string;
  translit: string;
  meaning: Record<string, string>;
  source: string;
}

const DAILY_DUAS: RamadanDua[] = [
  {
    arabic: 'اللَّهُمَّ إِنَّكَ عَفُوٌّ تُحِبُّ الْعَفْوَ فَاعْفُ عَنِّي',
    translit: "Allahumma innaka 'afuwwun tuhibbul 'afwa fa'fu 'anni",
    meaning: {
      en: 'O Allah, You are All-Forgiving, You love forgiveness, so forgive me.',
      fr: 'Ô Allah, Tu es Pardonneur, Tu aimes le pardon, alors pardonne-moi.',
      ar: 'اللَّهُمَّ إِنَّكَ عَفُوٌّ تُحِبُّ الْعَفْوَ فَاعْفُ عَنِّي',
      es: 'Oh Allah, Tú eres el Indulgente, amas el perdón, perdóname.',
      de: 'O Allah, Du bist der Verzeihende, Du liebst das Verzeihen, so verzeih mir.',
    },
    source: 'Tirmidhi 3513 (sahih)',
  },
  {
    arabic: 'رَبَّنَا تَقَبَّلْ مِنَّا إِنَّكَ أَنْتَ السَّمِيعُ الْعَلِيمُ',
    translit: "Rabbana taqabbal minna innaka antas-sami'ul 'alim",
    meaning: {
      en: 'Our Lord, accept from us. Indeed, You are the Hearing, the Knowing.',
      fr: 'Notre Seigneur, accepte de nous. C\'est Toi l\'Audient, l\'Omniscient.',
      ar: 'رَبَّنَا تَقَبَّلْ مِنَّا إِنَّكَ أَنْتَ السَّمِيعُ الْعَلِيمُ',
      es: 'Señor nuestro, acéptanos. Tú eres el que escucha, el que sabe.',
      de: 'Unser Herr, nimm es von uns an. Du bist der Hörende, der Wissende.',
    },
    source: 'Qur\'an 2:127',
  },
  {
    arabic: 'اللَّهُمَّ بَارِكْ لَنَا فِي رَجَبَ وَشَعْبَانَ وَبَلِّغْنَا رَمَضَانَ',
    translit: "Allahumma barik lana fi rajaba wa sha'bana wa balligna ramadan",
    meaning: {
      en: 'O Allah, bless us in Rajab and Shaʿbān and let us reach Ramadan.',
      fr: 'Ô Allah, bénis-nous en Rajab et Sha\'bān et fais-nous atteindre Ramadan.',
      ar: 'اللَّهُمَّ بَارِكْ لَنَا فِي رَجَبَ وَشَعْبَانَ وَبَلِّغْنَا رَمَضَانَ',
      es: 'Oh Allah, bendícenos en Rayab y Shaʿbān y haznos llegar a Ramadán.',
      de: 'O Allah, segne uns in Rajab und Shaʿbān und lasse uns Ramadan erreichen.',
    },
    source: 'Ahmad 2346',
  },
  {
    arabic: 'اللَّهُمَّ لَكَ صُمْتُ وَعَلَى رِزْقِكَ أَفْطَرْتُ',
    translit: "Allahumma laka sumtu wa 'ala rizqika aftartu",
    meaning: {
      en: 'O Allah, for You I fasted and with Your provision I broke my fast.',
      fr: 'Ô Allah, pour Toi j\'ai jeûné et avec Ta provision j\'ai rompu mon jeûne.',
      ar: 'اللَّهُمَّ لَكَ صُمْتُ وَعَلَى رِزْقِكَ أَفْطَرْتُ',
      es: 'Oh Allah, por Ti he ayunado y con Tu provisión rompí mi ayuno.',
      de: 'O Allah, für Dich habe ich gefastet und mit Deiner Versorgung mein Fasten gebrochen.',
    },
    source: 'Abu Dawud 2358',
  },
];

function pickDua(d: Date): RamadanDua {
  const seed = d.getDate();
  return DAILY_DUAS[seed % DAILY_DUAS.length];
}

interface MomentTarget {
  labelKey: string;
  target: Date;
  emoji: string;
}

function pickMoment(now: Date, times: { fajr: Date; maghrib: Date; isha: Date }): MomentTarget {
  const h = now.getHours();
  if (h < 5) return { labelKey: 'ramadan_suhoor_ends', target: times.fajr, emoji: '🌅' };
  if (h < 12) return { labelKey: 'ramadan_iftar_in', target: times.maghrib, emoji: '🌇' };
  if (h < 19) return { labelKey: 'ramadan_iftar_in', target: times.maghrib, emoji: '🌇' };
  if (h < 22) return { labelKey: 'ramadan_taraweeh_in', target: times.isha, emoji: '🕯' };
  return { labelKey: 'ramadan_suhoor_in', target: times.fajr, emoji: '🌙' };
}

function minutesUntil(now: Date, target: Date): number {
  let diffMs = target.getTime() - now.getTime();
  if (diffMs < 0) diffMs += 24 * 60 * 60 * 1000;
  return Math.round(diffMs / 60000);
}

function fmtHHmm(d: Date, locale: string): string {
  try { return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }); }
  catch { return d.toTimeString().slice(0, 5); }
}

function ramadanDay(d: Date): { day: number; total: number } | null {
  try {
    const h = gregorianToHijri(d);
    if (h.month !== 9) return null;
    return { day: h.day, total: 30 }; // close enough — 29 or 30 by year
  } catch {
    return null;
  }
}

export const RamadanDashboard: Widget = {
  id: 'ramadan-dashboard',
  label: {
    en: 'Ramadan',
    fr: 'Ramadan',
    ar: 'رمضان',
    es: 'Ramadán',
    de: 'Ramadan',
  },
  icon: '🌙',
  defaultVisible: true,
  defaultOrder: 25,
  defaultSize: 'compact',
  visibilityRule: () => {
    try { return gregorianToHijri(new Date()).month === 9; } catch { return false; }
  },

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    void size;
    const t = ctx.t;
    const day = ramadanDay(new Date());
    const times = prayerTimeService.getTodayTimes();

    if (!day || !times) {
      container.innerHTML = '';
      return;
    }

    const moment = pickMoment(new Date(), {
      fajr: times.fajr,
      maghrib: times.maghrib,
      isha: times.isha,
    });
    const minutes = minutesUntil(new Date(), moment.target);
    const dua = pickDua(new Date());
    const lang = (ctx.locale || 'en').split('-')[0];

    // Tarāwīḥ rakaat counter — store per (Hijri year, day).
    const trackerKey = `aweni_taraweeh_${day.day}`;
    let rakaat = 0;
    try { rakaat = parseInt(localStorage.getItem(trackerKey) || '0', 10) || 0; } catch { /* */ }

    const pct = Math.min(100, Math.round((day.day / day.total) * 100));

    container.innerHTML = `
      <div style="background:linear-gradient(135deg, #1e1b4b 0%, #312e81 100%);color:#fff;border-radius:16px;padding:14px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
          <span style="font-size:18px;">🌙</span>
          <span style="font-size:13px;font-weight:700;">${escapeHtml(t('widget_ramadan_title') || 'Ramadan')}</span>
          <span style="margin-inline-start:auto;font-size:11px;opacity:0.85;">${escapeHtml(t('widget_ramadan_day') || 'Day')} ${day.day}/${day.total}</span>
        </div>

        <!-- Big countdown -->
        <div style="text-align:center;padding:14px 0;">
          <div style="font-size:36px;font-weight:900;font-variant-numeric:tabular-nums;line-height:1;">${moment.emoji} ${formatDuration(minutes)}</div>
          <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.5px;opacity:0.85;margin-top:6px;">${escapeHtml(t(moment.labelKey) || moment.labelKey)} · ${escapeHtml(fmtHHmm(moment.target, ctx.locale))}</div>
        </div>

        <!-- Day progress bar -->
        <div style="height:4px;background:rgba(255,255,255,0.18);border-radius:999px;overflow:hidden;margin:0 0 12px;">
          <div style="height:100%;width:${pct}%;background:#fbbf24;"></div>
        </div>

        <!-- Tarāwīḥ tracker -->
        <div style="background:rgba(255,255,255,0.08);border-radius:10px;padding:10px;margin-bottom:10px;">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
            <span style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.4px;opacity:0.85;flex:1;">${escapeHtml(t('widget_ramadan_taraweeh') || 'Tarāwīḥ today')}</span>
            <span style="font-size:14px;font-weight:800;font-variant-numeric:tabular-nums;">${rakaat} ${escapeHtml(t('widget_ramadan_rakaat') || 'rakʿah')}</span>
          </div>
          <div style="display:flex;gap:6px;">
            <button type="button" data-act="track-add" style="flex:1;min-height:36px;border:none;border-radius:8px;background:#fbbf24;color:#0a0a0a;font-weight:700;cursor:pointer;font-size:12px;">+2 rakʿah</button>
            <button type="button" data-act="track-clear" style="min-height:36px;padding:0 12px;border:1px solid rgba(255,255,255,0.25);border-radius:8px;background:transparent;color:#fff;cursor:pointer;font-size:12px;">⟲</button>
          </div>
        </div>

        <!-- Daily du'a -->
        <div style="background:rgba(255,255,255,0.08);border-radius:10px;padding:10px;">
          <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.4px;opacity:0.85;margin-bottom:6px;">${escapeHtml(t('widget_ramadan_dua_today') || "Today's du'a")}</div>
          <div style="font-size:18px;font-weight:600;line-height:1.7;font-family:'Amiri','Scheherazade New',serif;text-align:center;direction:rtl;margin-bottom:6px;">${escapeHtml(dua.arabic)}</div>
          <div style="font-size:11px;opacity:0.85;font-style:italic;text-align:center;">${escapeHtml(dua.translit)}</div>
          <div style="font-size:12px;line-height:1.5;margin-top:6px;">${escapeHtml(dua.meaning[lang] || dua.meaning.en)}</div>
          <div style="font-size:10px;opacity:0.7;margin-top:4px;">— ${escapeHtml(dua.source)}</div>
        </div>
      </div>
    `;

    container.querySelector('[data-act="track-add"]')?.addEventListener('click', () => {
      rakaat = Math.min(40, rakaat + 2);
      try { localStorage.setItem(trackerKey, String(rakaat)); } catch { /* */ }
      // Re-render
      RamadanDashboard.render(container, size, ctx);
    });
    container.querySelector('[data-act="track-clear"]')?.addEventListener('click', () => {
      rakaat = 0;
      try { localStorage.setItem(trackerKey, '0'); } catch { /* */ }
      RamadanDashboard.render(container, size, ctx);
    });
  },
};

function formatDuration(min: number): string {
  if (min < 0) min = 0;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m}min`;
  return `${h}h ${m}min`;
}

function escapeHtml(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
